import {useContext, useEffect, useState} from 'react';
import {
  matchPath,
  Redirect,
  Route,
  Switch,
  useHistory,
  useParams,
  useRouteMatch,
} from 'react-router-dom';
import {Entity} from '../../../../../../backend/interfaces/entity';
import {ContentPage} from '../../../../../../components/content-page/content-page';
import {SectionTabs} from '../../../../../../components/section-tabs/section-tabs';
import {SnackBarContext} from '../../../../../../contexts/snack-bar/context';
import {EntitiesRepository} from '../../../../../../repositories/entities';
import {Box} from '@mui/material';
import {SnackBarContextType} from '../../../../../../contexts/snack-bar/types';
import {translations} from '../../../../../../i18n/translations';
import {useTranslation} from 'react-i18next';
import {OfficeContext} from '../../../../../../contexts/office/context';
import {OfficeContextType} from '../../../../../../contexts/office/types';
import {EntityType} from '../../../../../../backend/enums/entity-type';
import {AuthContextType} from '../../../../../../contexts/auth/types';
import {AuthContext} from '../../../../../../contexts/auth/context';
import {SubmitButtonState} from '../../../../../../components/submit-button/submit-button';
import {AppContextType} from '../../../../../../contexts/app/types';
import {AppContext} from '../../../../../../contexts/app/context';
import EntityDetailsSystemAdministratorOfficeScreen from './details/entity-details.system-administrator.screen';
import EntityActivitySystemAdministratorOfficeScreen from './activity/entity-activity.system-administrator.screen';
import EntityStatisticsSystemAdministratorOfficeScreen from './statistics/entity-statistics.system-administrator.screen';
import EntityApplicationsSystemAdministratorOfficeScreen from './applications/entity-applications.system-administrator.screen';
import EntityUserCardsSystemAdministratorOfficeScreen from './user-cards/entity-user-cards.system-administrator.screen';
import EntityDevicesSystemAdministratorOfficeScreen from './devices/entity-devices.system-administrator.screen';
import EntityLicensesSystemAdministratorOfficeScreen from './licenses/entity-licenses.system-administrator.screen';
import EntitySeniorsSystemAdministratorOfficeScreen from './seniors/entity-seniors.system-administrator.screen';
import EntityProfessionalCaregiversSystemAdministratorOfficeScreen from './professional-caregivers/entity-professional-caregivers.system-administrator.screen';
import EntityProfessionalCaregiverProfileSystemAdministratorOfficeScreen from './professional-caregivers/entity-professional-caregiver-profile.system-administrator.screen';
import EntityInviteProfessionalCaregiverSystemAdministratorOfficeScreen from './invite-professional-caregiver/entity-invite-professional-caregiver.system-administrator.screen';
import EntityPlaylistsSystemAdministratorOfficeScreen from './playlists/entity-playlists.system-administrator.screen';
import EntityPlaylistItemsSystemAdministratorOfficeScreen from './playlist-items/entity-playlist-items.system-administrator.screen';
import EntityFrontCardDesignToolSystemAdministratorOfficeScreen from './front-card-design-tool/front-card-design-tool.system-administrator.screen';
import EntityBackCardDesignToolSystemAdministratorOfficeScreen from './back-card-design-tool/back-card-design-tool.system-administrator.screen';

enum EntitySectionTabs {
  Details = 'details',
  Seniors = 'seniors',
  ProfessionalCaregivers = 'professional-caregivers',
  Applications = 'applications',
  Devices = 'devices',
  UserCards = 'user-cards',
  Activity = 'activity',
  Statistics = 'statistics',
  Licenses = 'licenses',
  Playlists = 'playlists',
}

enum PathExtensions {
  Seniors = 'seniors',
  ProfessionalCaregivers = 'professional-caregivers',
  ProfessionalCaregiverProfile = 'professional-caregivers/:professionalId',
  InviteProfessionalCaregiver = 'professional-caregivers/invite',
  Applications = 'applications',
  Devices = 'devices',
  UserCards = 'user-cards',
  Activity = 'activity',
  Statistics = 'statistics',
  Licenses = 'licenses',
  Playlists = 'playlists',
  PlaylistItems = 'playlists/:playlistId',
  FrontCardDesignTool = 'front-design-tool',
  BackCardDesignTool = 'back-design-tool',
}

export default function EntitySystemAdministratorOfficeScreen() {
  let {id}: {id?: string} = useParams();
  if (id?.toLowerCase() === 'create') {
    id = undefined;
  }
  let {path, url} = useRouteMatch();
  let history = useHistory();
  const {t} = useTranslation();
  const snackBarContext: SnackBarContextType = useContext(SnackBarContext);
  const officeContext: OfficeContextType = useContext(OfficeContext);
  const authContext: AuthContextType = useContext(AuthContext);
  const appContext: AppContextType = useContext(AppContext);

  // Get current tab based on the location
  const handleGetCurrentTab = (pathname: string) => {
    if (
      matchPath(pathname, {
        path: `${path}/${PathExtensions.Activity}`,
        exact: true,
        strict: false,
      })
    ) {
      return EntitySectionTabs.Activity;
    } else if (
      matchPath(pathname, {
        path: `${path}/${PathExtensions.Statistics}`,
        exact: true,
        strict: false,
      })
    ) {
      if (authContext.state.session?.systemAdministratorProfile?.analyticsAdmin === true) {
        return EntitySectionTabs.Statistics;
      } else {
        // Default
        return EntitySectionTabs.Details;
      }
    } else if (
      matchPath(pathname, {
        path: `${path}/${PathExtensions.Applications}`,
        exact: true,
        strict: false,
      })
    ) {
      return EntitySectionTabs.Applications;
    } else if (
      matchPath(pathname, {
        path: `${path}/${PathExtensions.UserCards}`,
        exact: true,
        strict: false,
      })
    ) {
      return EntitySectionTabs.UserCards;
    } else if (
      matchPath(pathname, {
        path: `${path}/${PathExtensions.Devices}`,
        exact: true,
        strict: false,
      })
    ) {
      return EntitySectionTabs.Devices;
    } else if (
      matchPath(pathname, {
        path: `${path}/${PathExtensions.InviteProfessionalCaregiver}`,
        exact: true,
        strict: false,
      })
    ) {
      return undefined;
    } else if (
      matchPath(pathname, {
        path: `${path}/${PathExtensions.ProfessionalCaregiverProfile}`,
        exact: false,
        strict: false,
      })
    ) {
      return EntitySectionTabs.ProfessionalCaregivers;
    } else if (
      matchPath(pathname, {
        path: `${path}/${PathExtensions.ProfessionalCaregivers}`,
        exact: true,
        strict: false,
      })
    ) {
      return EntitySectionTabs.ProfessionalCaregivers;
    } else if (
      matchPath(pathname, {
        path: `${path}/${PathExtensions.Seniors}`,
        exact: true,
        strict: false,
      })
    ) {
      return EntitySectionTabs.Seniors;
    } else if (
      matchPath(pathname, {
        path: `${path}/${PathExtensions.Licenses}`,
        exact: true,
        strict: false,
      })
    ) {
      return EntitySectionTabs.Licenses;
    } else if (
      matchPath(pathname, {
        path: `${path}/${PathExtensions.Playlists}`,
        exact: true,
        strict: false,
      })
    ) {
      return EntitySectionTabs.Playlists;
    } else if (
      matchPath(pathname, {
        path: `${path}/${PathExtensions.UserCards}/front-design-tool`,
        exact: true,
        strict: false,
      })
    ) {
      return EntitySectionTabs.UserCards;
    } else if (
      matchPath(pathname, {
        path: `${path}/${PathExtensions.UserCards}/back-design-tool`,
        exact: true,
        strict: false,
      })
    ) {
      return EntitySectionTabs.UserCards;
    } else if (
      matchPath(pathname, {
        path: `${path}/${PathExtensions.PlaylistItems}`,
        exact: true,
        strict: false,
      })
    ) {
      return undefined;
    }

    // Default
    return EntitySectionTabs.Details;
  };

  // Hold state
  const [currentTab, setCurrentTab] = useState<EntitySectionTabs | undefined>(
    handleGetCurrentTab(window.location.pathname)
  );

  // Listen for route changes
  useEffect(() => {
    // Listen for route changes
    const routeChangesUnsubscribe = history.listen((location: any) => {
      // Let's update the current tab
      setCurrentTab(handleGetCurrentTab(location.pathname));
    });

    // Clean up
    return () => {
      // Unsubscribe
      routeChangesUnsubscribe();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, path]);

  // Validate and get the entity
  useEffect(() => {
    const getEntity = async () => {
      if (id) {
        try {
          // Get entity
          const entityDetails: Entity = await EntitiesRepository.instance.getById(id);

          if (entityDetails) {
            // Set entity for edit/view
            officeContext.setEntityForm(entityDetails);
          } else {
            // Error
            throw new Error('Empty entity.');
          }
        } catch (error) {
          // Log
          console.debug('🔴 Could not get entity.', error);

          // Show toast error
          snackBarContext.setOfficeToastNotification({
            severity: 'error',
            message: t(translations.entities.edit.errors.entity_does_not_exist_error),
          });

          // Redirect
          history.push(`${url.replace(`/${id}`, '')}`);
        }
      } else {
        // Set entity for create
        officeContext.setEntityForm({
          name: '',
          product: appContext.state.configuration.product,
          isActive: true,
          type: EntityType.Generic,
        });
      }
    };

    // Get entity
    getEntity();

    // Clean up
    return () => {
      // Clear entity
      officeContext.clearEntityForm();

      // Log
      console.debug('🧹 Entity form cleaned in office context.');
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Statistics screen
  let statisticsTab: Array<{value: string; label: string; href: string}> = [];
  if (authContext.state.session?.systemAdministratorProfile?.analyticsAdmin === true) {
    statisticsTab.push({
      value: EntitySectionTabs.Statistics,
      label: t(translations.entities.edit.tabs.statistics),
      href:
        url.charAt(url.length - 1) === '/'
          ? `${url}${PathExtensions.Statistics}`
          : `${url}/${PathExtensions.Statistics}`,
    });
  }
  const renderPaths = () => {
    return (
      <Box
        sx={{
          background: '#FFFFFF',
          width: '100%',
          padding: {xs: '40px 10px 51px 10px', md: '40px 66px 51px 66px'},
          borderRadius: '10px',
        }}
      >
        {id && (
          <Box sx={{marginBottom: '16px'}}>
            <SectionTabs
              title={officeContext.state.entityForm?.name || ''}
              titleAvatar={
                currentTab !== EntitySectionTabs.Details
                  ? {
                      url: officeContext.state.entityForm?.picture,
                      type: 'entity',
                    }
                  : undefined
              }
              currentTab={currentTab}
              tabs={[
                {
                  value: EntitySectionTabs.Details,
                  label: t(translations.entities.edit.tabs.details),
                  href: url,
                },
                {
                  value: EntitySectionTabs.ProfessionalCaregivers,
                  label: t(translations.entities.edit.tabs.professional_caregivers),
                  href:
                    url.charAt(url.length - 1) === '/'
                      ? `${url}${PathExtensions.ProfessionalCaregivers}`
                      : `${url}/${PathExtensions.ProfessionalCaregivers}`,
                },
                {
                  value: EntitySectionTabs.Seniors,
                  label: t(translations.entities.edit.tabs.seniors),
                  href:
                    url.charAt(url.length - 1) === '/'
                      ? `${url}${PathExtensions.Seniors}`
                      : `${url}/${PathExtensions.Seniors}`,
                },
                {
                  value: EntitySectionTabs.Applications,
                  label: t(translations.entities.edit.tabs.applications),
                  href:
                    url.charAt(url.length - 1) === '/'
                      ? `${url}${PathExtensions.Applications}`
                      : `${url}/${PathExtensions.Applications}`,
                },
                {
                  value: EntitySectionTabs.Devices,
                  label: t(translations.entities.edit.tabs.devices),
                  href:
                    url.charAt(url.length - 1) === '/'
                      ? `${url}${PathExtensions.Devices}`
                      : `${url}/${PathExtensions.Devices}`,
                },
                {
                  value: EntitySectionTabs.UserCards,
                  label: t(translations.entities.edit.tabs.user_cards),
                  href:
                    url.charAt(url.length - 1) === '/'
                      ? `${url}${PathExtensions.UserCards}`
                      : `${url}/${PathExtensions.UserCards}`,
                },
                {
                  value: EntitySectionTabs.Licenses,
                  label: t(translations.entities.edit.tabs.licenses),
                  href:
                    url.charAt(url.length - 1) === '/'
                      ? `${url}${PathExtensions.Licenses}`
                      : `${url}/${PathExtensions.Licenses}`,
                },
                {
                  value: EntitySectionTabs.Activity,
                  label: t(translations.entities.edit.tabs.activity),
                  href:
                    url.charAt(url.length - 1) === '/'
                      ? `${url}${PathExtensions.Activity}`
                      : `${url}/${PathExtensions.Activity}`,
                },
                {
                  value: EntitySectionTabs.Playlists,
                  label: t(translations.entities.edit.tabs.playlists),
                  href:
                    url.charAt(url.length - 1) === '/'
                      ? `${url}${PathExtensions.Playlists}`
                      : `${url}/${PathExtensions.Playlists}`,
                },
                ...statisticsTab,
              ]}
              exitUrl={`${url.replace(`/${id}`, '')}`}
              disabled={
                officeContext.state.entityFormDeleteButtonState === SubmitButtonState.Loading ||
                officeContext.state.entityFormSubmitButtonState === SubmitButtonState.Loading ||
                officeContext.state.professionalCaregiverInviteFormSubmitButtonState ===
                  SubmitButtonState.Loading
              }
            />
          </Box>
        )}
        {/* Main content */}
        <Box>
          <Switch>
            {/* View/edit/create entity */}
            <Route exact path={path}>
              <EntityDetailsSystemAdministratorOfficeScreen />
            </Route>
            {/* Entity activity */}
            <Route exact path={`${path}/${PathExtensions.Activity}`}>
              <EntityActivitySystemAdministratorOfficeScreen />
            </Route>
            {/* Entity statistics */}
            {authContext.state.session?.systemAdministratorProfile?.analyticsAdmin === true && (
              <Route exact path={`${path}/${PathExtensions.Statistics}`}>
                <EntityStatisticsSystemAdministratorOfficeScreen />
              </Route>
            )}
            {/* Entity applications */}
            <Route exact path={`${path}/${PathExtensions.Applications}`}>
              <EntityApplicationsSystemAdministratorOfficeScreen />
            </Route>
            {/* Entity user cards */}
            <Route exact path={`${path}/${PathExtensions.UserCards}`}>
              <EntityUserCardsSystemAdministratorOfficeScreen />
            </Route>
            {/* Entity devices */}
            <Route exact path={`${path}/${PathExtensions.Devices}`}>
              <EntityDevicesSystemAdministratorOfficeScreen />
            </Route>
            {/* Entity seniors */}
            <Route exact path={`${path}/${PathExtensions.Seniors}`}>
              <EntitySeniorsSystemAdministratorOfficeScreen />
            </Route>
            {/* Entity professional caregivers */}
            <Route exact path={`${path}/${PathExtensions.ProfessionalCaregivers}`}>
              <EntityProfessionalCaregiversSystemAdministratorOfficeScreen />
            </Route>
            {/* Invite entity professional caregiver */}
            <Route exact path={`${path}/${PathExtensions.InviteProfessionalCaregiver}`}>
              <EntityInviteProfessionalCaregiverSystemAdministratorOfficeScreen />
            </Route>
            {/* Entity professional caregiver profile and entity-specific role assignment */}
            <Route path={`${path}/${PathExtensions.ProfessionalCaregiverProfile}`}>
              <EntityProfessionalCaregiverProfileSystemAdministratorOfficeScreen />
            </Route>
            {/* Entity licenses */}
            <Route exact path={`${path}/${PathExtensions.Licenses}`}>
              <EntityLicensesSystemAdministratorOfficeScreen />
            </Route>
            {/* Entity playlists */}
            <Route exact path={`${path}/${PathExtensions.Playlists}`}>
              <EntityPlaylistsSystemAdministratorOfficeScreen />
            </Route>
            {/* Entity playlists */}
            <Route exact path={`${path}/${PathExtensions.PlaylistItems}`}>
              <EntityPlaylistItemsSystemAdministratorOfficeScreen />
            </Route>

            {/* Front Card Design Tool */}
            <Route
              exact
              path={`${path}/${PathExtensions.UserCards}/${PathExtensions.FrontCardDesignTool}`}
            >
              <EntityFrontCardDesignToolSystemAdministratorOfficeScreen />
            </Route>

            {/* Back Card Design Tool */}
            <Route
              exact
              path={`${path}/${PathExtensions.UserCards}/${PathExtensions.BackCardDesignTool}`}
            >
              <EntityBackCardDesignToolSystemAdministratorOfficeScreen />
            </Route>

            {/** Default route */}
            <Route path={path}>
              <Redirect to={url} />
            </Route>
          </Switch>
        </Box>
      </Box>
    );
  };

  if (id) {
    return <ContentPage>{renderPaths()}</ContentPage>;
  }

  return (
    <>
      {id && (
        <Box sx={{marginBottom: '16px'}}>
          <SectionTabs
            title={officeContext.state.entityForm?.name || ''}
            titleAvatar={
              currentTab !== EntitySectionTabs.Details
                ? {
                    url: officeContext.state.entityForm?.picture,
                    type: 'entity',
                  }
                : undefined
            }
            currentTab={currentTab}
            tabs={[
              {
                value: EntitySectionTabs.Details,
                label: t(translations.entities.edit.tabs.details),
                href: url,
              },
              {
                value: EntitySectionTabs.ProfessionalCaregivers,
                label: t(translations.entities.edit.tabs.professional_caregivers),
                href:
                  url.charAt(url.length - 1) === '/'
                    ? `${url}${PathExtensions.ProfessionalCaregivers}`
                    : `${url}/${PathExtensions.ProfessionalCaregivers}`,
              },
              {
                value: EntitySectionTabs.Seniors,
                label: t(translations.entities.edit.tabs.seniors),
                href:
                  url.charAt(url.length - 1) === '/'
                    ? `${url}${PathExtensions.Seniors}`
                    : `${url}/${PathExtensions.Seniors}`,
              },
              {
                value: EntitySectionTabs.Applications,
                label: t(translations.entities.edit.tabs.applications),
                href:
                  url.charAt(url.length - 1) === '/'
                    ? `${url}${PathExtensions.Applications}`
                    : `${url}/${PathExtensions.Applications}`,
              },
              {
                value: EntitySectionTabs.Devices,
                label: t(translations.entities.edit.tabs.devices),
                href:
                  url.charAt(url.length - 1) === '/'
                    ? `${url}${PathExtensions.Devices}`
                    : `${url}/${PathExtensions.Devices}`,
              },
              {
                value: EntitySectionTabs.UserCards,
                label: t(translations.entities.edit.tabs.user_cards),
                href:
                  url.charAt(url.length - 1) === '/'
                    ? `${url}${PathExtensions.UserCards}`
                    : `${url}/${PathExtensions.UserCards}`,
              },
              {
                value: EntitySectionTabs.Licenses,
                label: t(translations.entities.edit.tabs.licenses),
                href:
                  url.charAt(url.length - 1) === '/'
                    ? `${url}${PathExtensions.Licenses}`
                    : `${url}/${PathExtensions.Licenses}`,
              },
              {
                value: EntitySectionTabs.Activity,
                label: t(translations.entities.edit.tabs.activity),
                href:
                  url.charAt(url.length - 1) === '/'
                    ? `${url}${PathExtensions.Activity}`
                    : `${url}/${PathExtensions.Activity}`,
              },
              {
                value: EntitySectionTabs.Playlists,
                label: t(translations.entities.edit.tabs.playlists),
                href:
                  url.charAt(url.length - 1) === '/'
                    ? `${url}${PathExtensions.Playlists}`
                    : `${url}/${PathExtensions.Playlists}`,
              },
              ...statisticsTab,
            ]}
            exitUrl={`${url.replace(`/${id}`, '')}`}
            disabled={
              officeContext.state.entityFormDeleteButtonState === SubmitButtonState.Loading ||
              officeContext.state.entityFormSubmitButtonState === SubmitButtonState.Loading ||
              officeContext.state.professionalCaregiverInviteFormSubmitButtonState ===
                SubmitButtonState.Loading
            }
          />
        </Box>
      )}
      {/* Main content */}
      <Box>
        <Switch>
          {/* View/edit/create entity */}
          <Route exact path={path}>
            <EntityDetailsSystemAdministratorOfficeScreen />
          </Route>
          {/* Entity activity */}
          <Route exact path={`${path}/${PathExtensions.Activity}`}>
            <EntityActivitySystemAdministratorOfficeScreen />
          </Route>
          {/* Entity statistics */}
          {authContext.state.session?.systemAdministratorProfile?.analyticsAdmin === true && (
            <Route exact path={`${path}/${PathExtensions.Statistics}`}>
              <EntityStatisticsSystemAdministratorOfficeScreen />
            </Route>
          )}
          {/* Entity applications */}
          <Route exact path={`${path}/${PathExtensions.Applications}`}>
            <EntityApplicationsSystemAdministratorOfficeScreen />
          </Route>
          {/* Entity user cards */}
          <Route exact path={`${path}/${PathExtensions.UserCards}`}>
            <EntityUserCardsSystemAdministratorOfficeScreen />
          </Route>
          {/* Entity devices */}
          <Route exact path={`${path}/${PathExtensions.Devices}`}>
            <EntityDevicesSystemAdministratorOfficeScreen />
          </Route>
          {/* Entity seniors */}
          <Route exact path={`${path}/${PathExtensions.Seniors}`}>
            <EntitySeniorsSystemAdministratorOfficeScreen />
          </Route>
          {/* Entity professional caregivers */}
          <Route exact path={`${path}/${PathExtensions.ProfessionalCaregivers}`}>
            <EntityProfessionalCaregiversSystemAdministratorOfficeScreen />
          </Route>
          {/* Invite entity professional caregiver */}
          <Route exact path={`${path}/${PathExtensions.InviteProfessionalCaregiver}`}>
            <EntityInviteProfessionalCaregiverSystemAdministratorOfficeScreen />
          </Route>
          {/* Entity licenses */}
          <Route exact path={`${path}/${PathExtensions.Licenses}`}>
            <EntityLicensesSystemAdministratorOfficeScreen />
          </Route>
          {/* Entity playlists */}
          <Route exact path={`${path}/${PathExtensions.Playlists}`}>
            <EntityPlaylistsSystemAdministratorOfficeScreen />
          </Route>
          {/* Entity playlists */}
          <Route exact path={`${path}/${PathExtensions.PlaylistItems}`}>
            <EntityPlaylistItemsSystemAdministratorOfficeScreen />
          </Route>

          {/* Front Card Design Tool */}
          <Route
            exact
            path={`${path}/${PathExtensions.UserCards}/${PathExtensions.FrontCardDesignTool}`}
          >
            <EntityFrontCardDesignToolSystemAdministratorOfficeScreen />
          </Route>

          {/* Back Card Design Tool */}
          <Route
            exact
            path={`${path}/${PathExtensions.UserCards}/${PathExtensions.BackCardDesignTool}`}
          >
            <EntityBackCardDesignToolSystemAdministratorOfficeScreen />
          </Route>

          {/** Default route */}
          <Route path={path}>
            <Redirect to={url} />
          </Route>
        </Switch>
      </Box>
    </>
  );
}
